import { ReactNode } from 'react';

interface BaseActionTicketProps {
  /** Couleur de la bordure gauche (hex ou tailwind color class) */
  borderColor: string;
  /** Couleur de fond (tailwind class comme 'bg-indigo-50') */
  backgroundColor: string;
  /** Icône ou thème à afficher (optionnel) */
  header?: ReactNode;
  /** Titre principal du ticket */
  title: string;
  /** Sous-titre ou texte secondaire (optionnel) */
  subtitle?: ReactNode;
  /** Contenu personnalisé du ticket (flexbox column) - optionnel */
  children?: ReactNode;
  /** Tags/badges à afficher (optionnel) */
  tags?: ReactNode;
  /** Hiérarchie optionnelle à afficher dans le coin (client/site/secteur/eventmanager) */
  hierarchy?: {
    client?: string;
    site?: string;
    sector?: string;
    eventManager?: string;
  };
  /** Footer avec infos supplémentaires (optionnel) */
  footer?: {
    left?: ReactNode;
    right?: ReactNode;
  };
  /** Callback pour l'événement click (optionnel) */
  onClick?: () => void;
}

export function BaseActionTicketComponent( {
  borderColor,
  backgroundColor,
  header,
  title,
  subtitle,
  children,
  tags,
  hierarchy,
  footer,
  onClick,
}: BaseActionTicketProps ) {
  const isHexColor = borderColor.startsWith( '#' );
  const borderStyle = isHexColor ? { borderLeft: `4px solid ${borderColor}` } : {};

  return (
    <div
      className={ `flex flex-col h-full p-4 rounded-lg ${backgroundColor} hover:shadow-md transition-shadow duration-200 ${ isHexColor ? '' : 'border-l-4' } relative ${ onClick ? 'cursor-pointer' : '' }` }
      style={ borderStyle }
      onClick={ onClick }
    >
      {/* Hiérarchie optionnelle dans le coin */}
      { hierarchy && (
        <div className='absolute top-2 right-2 text-xs text-gray-500 text-right space-y-0.5'>
          { hierarchy.client && <div>{ hierarchy.client }</div> }
          { hierarchy.site && <div>{ hierarchy.site }</div> }
          { hierarchy.sector && <div>{ hierarchy.sector }</div> }
          { hierarchy.eventManager && <div className='font-semibold'>{ hierarchy.eventManager }</div> }
        </div>
      ) }

      {/* Header: icône/thème optionnel */}
      { header && <div className='mb-3'>{ header }</div> }

      {/* Titre et sous-titre */}
      <div className='mb-3'>
        <h4 className='text-gray-800 font-semibold mb-1'>
          { title }
        </h4>
        { subtitle && <div className='text-sm text-gray-600'>{ subtitle }</div> }
      </div>

      {/* Tags/badges */}
      { tags && <div className='flex flex-wrap gap-2 mb-3'>{ tags }</div> }

      {/* Contenu personnalisé */}
      { children && <div className='flex flex-col gap-2 text-sm grow'>{ children }</div> }

      {/* Footer avec infos supplémentaires */}
      { footer && (
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 mt-auto border-t border-gray-300 text-xs text-gray-600'>
          <div className='min-w-0 sm:flex-1'>{ footer.left }</div>
          { footer.right && (
            <div className='flex items-center flex-wrap gap-1 sm:flex-shrink-0 sm:justify-end'>
              { footer.right }
            </div>
          ) }
        </div>
      ) }
    </div>
  );
}
